import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Crown, Users, Compass, Briefcase, Building2, Globe2, CheckCircle2, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
const practices = [{
  num: '01',
  icon: Crown,
  title: 'Executive Search',
  tagline: 'CEO, C-Suite & Senior Leadership',
  desc: 'Confidential, retained-search mandates for the most consequential hires. We partner with boards, investors, and CEOs to identify, assess, and secure the transformative leaders organizations need to navigate their next chapter.',
  capabilities: ['CEO succession & external recruitment', 'C-suite searches (CFO, CHRO, CTO, COO, CMO, CRO)', 'PE & VC portfolio leadership', 'Confidential cross-border placements']
}, {
  num: '02',
  icon: Users,
  title: 'Board & Director Services',
  tagline: 'Independent Directors & Board Composition',
  desc: 'A dedicated practice for board appointments, governance advisory, and board composition strategy. We help organizations build boards that bring sector depth, functional balance, and diversity of perspective.',
  capabilities: ['Independent director searches', 'Board composition assessment', 'Audit, compensation & nominating committees', 'Board diversity advisory']
}, {
  num: '03',
  icon: Compass,
  title: 'Leadership Advisory',
  tagline: 'Succession, Assessment & Onboarding',
  desc: 'Beyond placement, we advise organizations on the strategic leadership decisions that shape long-term performance — from CEO succession planning to executive assessment to onboarding integration.',
  capabilities: ['CEO & C-suite succession planning', 'Executive assessment & benchmarking', 'Leadership team effectiveness reviews', 'New-leader integration & onboarding']
}, {
  num: '04',
  icon: Briefcase,
  title: 'Resume & Career Positioning',
  tagline: 'Executive-Level Personal Branding',
  desc: 'A specialist offering for senior executives navigating their own next move. We provide strategic positioning, executive biography development, and LinkedIn architecture for leaders who require the highest level of discretion.',
  capabilities: ['Executive resume & biography development', 'LinkedIn architecture for C-suite leaders', 'Board appointment positioning', 'Confidential career strategy sessions']
}, {
  num: '05',
  icon: Building2,
  title: 'Talent Acquisition Consulting',
  tagline: 'Enterprise TA Strategy & Function Design',
  desc: 'For organizations building or scaling internal talent acquisition capability, we advise on function design, employer branding, recruitment technology, and high-volume hiring strategy at the enterprise level.',
  capabilities: ['In-house TA function design', 'Recruitment process optimization', 'Employer brand strategy', 'Hiring manager enablement']
}, {
  num: '06',
  icon: Globe2,
  title: 'Global Mobility & Cross-Border',
  tagline: 'International Placement & Relocation',
  desc: 'A practice dedicated to cross-border executive moves. We handle the complexity of multi-jurisdiction placements — including work authorization advisory, cultural integration, and relocation support.',
  capabilities: ['Cross-border executive placement', 'Work authorization & visa advisory', 'Cultural & market briefings', 'Spouse & family relocation support']
}];
const engagementModels = [{
  name: 'Retained Search',
  desc: 'Exclusive, retained partnership for C-suite, board, and senior leadership mandates. Our flagship engagement model.',
  fee: 'Tiered retainer + completion fee',
  timeline: '90–120 days',
  best: 'CEO, C-suite, board, sensitive cross-border'
}, {
  name: 'Engaged Search',
  desc: 'Mid-flexibility engagement for senior leadership roles below the C-suite. Combines rigor of retained with broader access.',
  fee: 'Partial retainer + placement fee',
  timeline: '60–90 days',
  best: 'VP, SVP, and senior director roles'
}, {
  name: 'Advisory Engagement',
  desc: 'Project-based consulting for succession, assessment, and leadership-team advisory work — independent of any search.',
  fee: 'Fixed project fee',
  timeline: '30–90 days',
  best: 'Succession planning, assessment, governance'
}];
export function ServicesPage() {
  return <div className="bg-white" data-id="element-718">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="relative bg-navy-900 text-white pt-32 pb-24 overflow-hidden" data-id="element-719">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" data-id="element-720" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12" data-id="element-721">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end" data-id="element-722">
            <div className="lg:col-span-8" data-id="element-723">
              <div className="flex items-center gap-3 mb-8" data-id="element-724">
                <div className="w-12 h-px bg-gold-500" data-id="element-725" />
                <span className="text-gold-500 font-semibold tracking-[0.3em] uppercase text-xs" data-id="element-726">
                  Practice Areas
                </span>
              </div>
              <motion.h1 initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8
            }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold leading-[1.05] mb-8 tracking-tight" data-id="element-727">
                Advisory built around the{' '}
                <span className="italic text-gold-500" data-id="element-728">most consequential</span>{' '}
                leadership hires.
              </motion.h1>
            </div>
            <div className="lg:col-span-4" data-id="element-729">
              <p className="text-xl text-gray-300 leading-relaxed font-light" data-id="element-730">
                Six integrated practice areas. One commitment: helping you
                identify and secure the leaders who define your organization's
                next decade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRACTICES — Detailed Cards ───────────────────────── */}
      <section className="py-28 bg-white" data-id="element-731">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-732">
          <div className="space-y-px bg-gray-200" data-id="element-733">
            {practices.map((practice, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-white p-10 md:p-14 group hover:bg-gray-50 transition-colors duration-500" data-id="element-734">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-id="element-735">
                  {/* Number + Icon */}
                  <div className="lg:col-span-2" data-id="element-736">
                    <div className="flex lg:flex-col items-center lg:items-start gap-4" data-id="element-737">
                      <span className="text-5xl font-serif text-gold-500" data-id="element-738">
                        {practice.num}
                      </span>
                      <practice.icon className="h-8 w-8 text-navy-900 lg:mt-4" data-id="element-739" />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="lg:col-span-7" data-id="element-740">
                    <div className="text-xs uppercase tracking-[0.25em] text-gold-600 font-semibold mb-3" data-id="element-741">
                      {practice.tagline}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 leading-tight mb-5" data-id="element-742">
                      {practice.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed" data-id="element-743">
                      {practice.desc}
                    </p>
                  </div>

                  {/* Capabilities */}
                  <div className="lg:col-span-3 lg:border-l lg:border-gray-200 lg:pl-8" data-id="element-744">
                    <div className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4" data-id="element-745">
                      Capabilities
                    </div>
                    <ul className="space-y-3" data-id="element-746">
                      {practice.capabilities.map((cap, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-747">
                          <CheckCircle2 className="h-4 w-4 text-gold-500 mt-0.5 flex-shrink-0" data-id="element-748" />
                          <span data-id="element-749">{cap}</span>
                        </li>)}
                    </ul>
                    <a href="/contact" className="inline-flex items-center gap-2 mt-6 text-navy-900 font-semibold text-sm hover:text-gold-600 transition-colors group/link" data-id="element-750">
                      Discuss this practice
                      <ArrowUpRight className="h-4 w-4 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" data-id="element-751" />
                    </a>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── SECTOR EXPERTISE STRIP ───────────────────────────── */}
      <section className="py-20 bg-navy-900 text-white" data-id="element-752">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-753">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12" data-id="element-754">
            <div className="lg:col-span-5" data-id="element-755">
              <div className="flex items-center gap-3 mb-6" data-id="element-756">
                <div className="w-12 h-px bg-gold-500" data-id="element-757" />
                <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-758">
                  Sector Depth
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold leading-tight" data-id="element-759">
                Every practice, led by sector specialists.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7" data-id="element-760">
              <p className="text-gray-300 leading-relaxed text-lg" data-id="element-761">
                Our consultants bring operating experience in the industries
                they serve. They speak the language, know the talent, and
                understand the strategic forces shaping each sector.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10" data-id="element-762">
            {['Technology', 'Financial Services', 'Healthcare & Life Sciences', 'Industrial & Manufacturing', 'Aerospace & Defense', 'AI & Emerging Tech', 'Energy & Sustainability', 'Consumer & Retail', 'Professional Services', 'Private Equity', 'Government & Public Sector', 'Real Estate'].map((sector, i) => <div key={i} className="bg-navy-900 p-5 hover:bg-navy-800 transition-colors text-sm font-medium tracking-tight" data-id="element-763">
                {sector}
              </div>)}
          </div>
        </div>
      </section>

      {/* ─── ENGAGEMENT MODELS ────────────────────────────────── */}
      <section className="py-28 bg-gray-50" data-id="element-764">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-765">
          <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-766">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-767">
              <div className="w-12 h-px bg-gold-500" data-id="element-768" />
              <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-769">
                Engagement Models
              </span>
              <div className="w-12 h-px bg-gold-500" data-id="element-770" />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-6" data-id="element-771">
              Three ways to engage our firm.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-772">
              We tailor the engagement model to the consequence of the mandate.
              Pricing is transparent and structured around outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200" data-id="element-773">
            {engagementModels.map((model, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-10" data-id="element-774">
                <h3 className="text-2xl font-serif font-bold text-navy-900 mb-3" data-id="element-775">
                  {model.name}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8 text-sm" data-id="element-776">
                  {model.desc}
                </p>

                <div className="space-y-5 pt-6 border-t border-gray-100" data-id="element-777">
                  <div data-id="element-778">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-779">
                      Fee Structure
                    </div>
                    <div className="text-navy-900 font-semibold text-sm" data-id="element-780">
                      {model.fee}
                    </div>
                  </div>
                  <div data-id="element-781">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-782">
                      Typical Timeline
                    </div>
                    <div className="text-navy-900 font-semibold text-sm" data-id="element-783">
                      {model.timeline}
                    </div>
                  </div>
                  <div data-id="element-784">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-785">
                      Best Suited For
                    </div>
                    <div className="text-navy-900 font-semibold text-sm" data-id="element-786">
                      {model.best}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-12" data-id="element-787">
            <p className="text-sm text-gray-500 italic" data-id="element-788">
              Unsure which model fits your mandate? We will help you scope it in
              a confidential conversation.
            </p>
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────── */}
      <section className="py-28 bg-white" data-id="element-789">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center" data-id="element-790">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-791">
            <div className="w-12 h-px bg-gold-500" data-id="element-792" />
            <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-793">
              Begin a Conversation
            </span>
            <div className="w-12 h-px bg-gold-500" data-id="element-794" />
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-6" data-id="element-795">
            Let's scope your mandate.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-796">
            Our senior consultants are available for a confidential,
            no-obligation scoping conversation. We will respond within one
            business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-797">
            <Button variant="gold" size="lg" href="mailto:contact@talentbridge.blog" data-id="element-798">
              Request a Consultation
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-799" />
            </Button>
            <Button variant="outline" size="lg" href="/how-it-works" data-id="element-800">
              See Our Process
            </Button>
          </div>
        </div>
      </section>
    </div>;
}